// Phase 5 (web revival) — Bearer-authed thumbnail tile.
//
// The /v1/assets/{id}/thumbnail endpoint requires the Authorization
// header, which `<img src>` can't set. We fetch the bytes via the API
// client (gets the Bearer attached + handles 401 refresh) into a Blob,
// then point the `<img>` at the resulting `blob:` URL.
//
// IMPORTANT: every `URL.createObjectURL` must be paired with a
// `URL.revokeObjectURL` on unmount. The library page renders thousands
// of these as the user scrolls; without revocation, blob URLs pin
// their underlying Blobs in memory and the tab balloons.

import { useEffect, useState } from 'react';
import { prefetchAuthedImage } from '../../api/client';

interface ThumbnailProps {
  assetID: string;
  /// Pixel size of the longest edge the server should render. Server
  /// caches by (asset, size), so picking from a small whitelist
  /// (128 / 256 / 512) keeps the derivative cache compact.
  size: number;
  /// Optional className appended to the `<img>` tag; the wrapper sizes
  /// itself to fill its parent.
  className?: string;
  /// Alt text for accessibility; default '' (decorative).
  alt?: string;
  /// Cache-buster. Thumbnails are served `immutable` for a year, so when
  /// the bytes change under a stable URL (the user rotated the photo and
  /// the server re-rendered it) the URL has to change too. Pass the
  /// asset's `display_rotation`.
  version?: number | string;
}

export function Thumbnail({ assetID, size, className, alt = '', version }: ThumbnailProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdURL: string | null = null;
    setFailed(false);
    setUrl(null);

    void (async () => {
      try {
        const blobURL = await prefetchAuthedImage(
          `/v1/assets/${assetID}/thumbnail?size=${size}` +
            (version ? `&v=${version}` : ''),
        );
        if (cancelled) {
          // Component unmounted while the fetch was in flight; revoke
          // the just-allocated URL so the Blob isn't leaked.
          URL.revokeObjectURL(blobURL);
          return;
        }
        createdURL = blobURL;
        setUrl(blobURL);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (createdURL) URL.revokeObjectURL(createdURL);
    };
  }, [assetID, size, version]);

  if (failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-900 text-neutral-700">
        <span className="text-xs">!</span>
      </div>
    );
  }

  if (!url) {
    return <div className="h-full w-full animate-pulse bg-neutral-900" />;
  }

  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={
        className ??
        'h-full w-full object-cover'
      }
    />
  );
}
